// ════════════════════════════════════════════════════════
//  Утилитные функции для AI-Pirogov-dev
// ════════════════════════════════════════════════════════

/** Получить значение cookie по имени */
export function getCookie(name) {
  if (!document.cookie) return null;
  const match = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.substring(name.length + 1)) : null;
}

/** Установить cookie */
export function setCookieValue(name, value, days = 365) {
  const date = new Date();
  date.setTime(date.getTime() + days * 86_400_000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${date.toUTCString()};path=/`;
}

/** Числовой ID → строка с нулями слева (0001, 0012, …) */
export function formatCaseId(id) {
  return String(id).padStart(4, '0');
}

/** Blob → base64 data-URL строка */
export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** Конвертирует произвольный аудио-Blob (webm/ogg) в WAV */
export async function convertToWav(blob) {
  const arrayBuffer = await blob.arrayBuffer();
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

  const numChannels = audioBuffer.numberOfChannels;
  const length = audioBuffer.length * numChannels * 2 + 44;
  const buffer = new ArrayBuffer(length);
  const view = new DataView(buffer);
  let offset = 0;

  const write16 = (v) => { view.setUint16(offset, v, true); offset += 2; };
  const write32 = (v) => { view.setUint32(offset, v, true); offset += 4; };

  // RIFF header
  write32(0x46464952);               // "RIFF"
  write32(length - 8);               // file length - 8
  write32(0x45564157);               // "WAVE"

  // fmt chunk
  write32(0x20746d66);               // "fmt "
  write32(16);                       // chunk size
  write16(1);                        // PCM
  write16(numChannels);
  write32(audioBuffer.sampleRate);
  write32(audioBuffer.sampleRate * 2 * numChannels); // byte rate
  write16(numChannels * 2);          // block align
  write16(16);                       // bits per sample

  // data chunk
  write32(0x61746164);               // "data"
  write32(length - 44);              // data size

  const channels = Array.from({ length: numChannels }, (_, i) => audioBuffer.getChannelData(i));

  for (let pos = 0; pos < audioBuffer.length; pos++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = Math.max(-1, Math.min(1, channels[ch][pos]));
      view.setInt16(offset, (0.5 + (sample < 0 ? sample * 32768 : sample * 32767)) | 0, true);
      offset += 2;
    }
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

/**
 * Построить объект conversation_history для API-запросов.
 * Используется и в handleUpdateSubmit, и в handleClarificationSubmit.
 */
export function buildConversationHistory({ query, scenePlanningInfo, updateHistory, offTopicCount }) {
  return {
    original_query: query,
    scene_rationale: scenePlanningInfo
      ? (typeof scenePlanningInfo.rationale === 'string'
          ? scenePlanningInfo.rationale
          : scenePlanningInfo.rationale?.name || JSON.stringify(scenePlanningInfo))
      : '',
    edit_history: updateHistory.map((item) => {
      // Determine type
      let type;
      if (item.sceneGenerated) type = 'generation';
      else if (item.offTopicResponse) type = 'ai_response';
      else type = 'scene_edit';

      // Build result_summary: concise but verbatim for referencing
      let result_summary = '';
      if (item.offTopicResponse) {
        // AI text response (medical/informational/off-topic) — verbatim
        result_summary = item.offTopicResponse;
      } else if (item.completionReport) {
        // Scene edit — concise summary from completion report
        const parts = [];
        if (item.completionReport.done?.length) parts.push(...item.completionReport.done);
        if (item.completionReport.failed?.length) parts.push(...item.completionReport.failed);
        result_summary = parts.join('; ');
      } else if (item.rationale) {
        result_summary = item.rationale;
      }

      return { query: item.query, result_summary, type };
    }),
    off_topic_count: offTopicCount,
  };
}

/** Скачать файл по URL с токеном авторизации */
export async function downloadFile(url, apiToken) {
  const filename = url.split('/').pop();
  const response = await fetch(url, {
    headers: { 'X-Auth-Token': apiToken },
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Download failed');
  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}
